import React, { useEffect, useRef, useState, useContext } from 'react';
import { useChatbotStore } from '../../store/chatbot.store';
import ChatMessage from './ChatMessage'; // Import from the same directory
import SecondaryFilterSelection from './SecondaryFilterSelection';
import { filterService } from '../../services/filterService';
import { useCollectionCache } from '../../services/collectionCache.service';
import './chatbot.scss';
import { useAuthStore } from '../../store/auth.store';
import { FirebaseContext } from '../../components/Firebase';
import { audioAnalysisService } from '../../services/audioAnalysisService';
import { youtubeAudioExtractor } from '../../services/youtubeAudioExtractor';

interface ChatbotPanelProps {
  onApplyFilters: (filters: any) => void;
  useSimplePrompts?: boolean;
}

const ChatbotPanel: React.FC<ChatbotPanelProps> = ({ onApplyFilters, useSimplePrompts = false }) => {
const { isOpen, addMessage, config, fetchConfig, clearChat, getVisibleMessages, addFilterResetFlag } = useChatbotStore();
  const messages = getVisibleMessages();
  const collectionCache = useCollectionCache();
  const [input, setInput] = useState('');
const [processingQuery, setProcessingQuery] = useState(false);
const [secondaryMatches, setSecondaryMatches] = useState<any[]>([]);
const [showSecondarySelection, setShowSecondarySelection] = useState(false);
const [pendingSingleSecondaryMatches, setPendingSingleSecondaryMatches] = useState<any[]>([]);
const currentPrimaryFiltersRef = useRef<any>(null);
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const [isShowingSelectionUI, setIsShowingSelectionUI] = useState(false);
const messagesEndRef = useRef<HTMLDivElement>(null);
const textareaRef = useRef<HTMLTextAreaElement>(null);
const selectionUIRef = useRef<HTMLDivElement>(null);
const auth = useAuthStore(state => state.auth);
const firebase = useContext(FirebaseContext);
let firstName = '';
if (auth && auth.username) {
  // Try to extract first name from username (assume space-separated)
  firstName = auth.username.split(' ')[0];
} else if (auth && auth.email) {
  // Fallback: use the part before @ in email
  firstName = auth.email.split('@')[0];
}
const [isScrolling, setIsScrolling] = useState(false);
const scrollAnimationRef = useRef<number | null>(null);

  // Audio analysis state
  const [audioAnalyzing, setAudioAnalyzing] = useState(false);
  // const [audioFile, setAudioFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch config on mount
  useEffect(() => {
    if (!config && firebase) {
      fetchConfig(firebase);
    }
  }, [config, fetchConfig, firebase]);

  useEffect(() => {
    if (textareaRef.current) {
      // Reset height to auto to get the correct scrollHeight
      textareaRef.current.style.height = 'auto';
      // Set the height to match the content
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [input]);

  // Automatically scroll to bottom when messages change, unless secondary selection UI is shown
  useEffect(() => {
    if (!showSecondarySelection) {
      scrollToBottom();
    }
  }, [messages, showSecondarySelection]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const cleanupScroll = () => {
    if (scrollAnimationRef.current) {
      cancelAnimationFrame(scrollAnimationRef.current);
      scrollAnimationRef.current = null;
    }
    setIsScrolling(false);
  };

  useEffect(() => {
    return () => {
      cleanupScroll();
    };
  }, []);

  const scrollToSelectionUI = () => {
    if (isScrolling) {
      cleanupScroll();
    }

    if (selectionUIRef.current) {
      const container = selectionUIRef.current.parentElement;
      if (container) {
        setIsScrolling(true);
        const targetPosition = selectionUIRef.current.offsetTop;
        const startPosition = container.scrollTop;
        const distance = targetPosition - startPosition;
        const duration = 36000;
        const startTime = performance.now();


        const animateScroll = (currentTime: number) => {
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / duration, 1);
          
          // Much slower easing function using a higher power
          const easeInOutQuint = (t: number) => {
            // Apply a very high power to slow down the progress significantly
            const slowedProgress = Math.pow(t, 4); // Using power of 4 to make it much slower
            // Apply easing to the slowed progress
            return t < 0.5
              ? 16 * slowedProgress * slowedProgress * slowedProgress * slowedProgress * slowedProgress
              : 1 - Math.pow(-2 * t + 2, 5) / 2;
          };
          
          const easedProgress = easeInOutQuint(progress);
          
          // Apply additional smoothing to the scroll position
          const currentScroll = startPosition + (distance * easedProgress);
          const smoothedScroll = container.scrollTop + (currentScroll - container.scrollTop) * 0.1; // Only move 10% of the remaining distance each frame
          
          container.scrollTop = smoothedScroll;


          if (progress < 1) {
            scrollAnimationRef.current = requestAnimationFrame(animateScroll);
          } else {
            cleanupScroll();
          }
        };

        scrollAnimationRef.current = requestAnimationFrame(animateScroll);
      } else {
        //console.log('[SCROLL_DEBUG] No container found');
      }
    } else {
      //console.log('[SCROLL_DEBUG] No selectionUIRef found');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Handle spacebar without triggering playback
    if (e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      const textarea = e.target as HTMLTextAreaElement;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      setInput(prev => prev.substring(0, start) + ' ' + prev.substring(end));
      // Restore cursor position after state update
      setTimeout(() => {
        textarea.selectionStart = start + 1;
        textarea.selectionEnd = start + 1;
      }, 0);
      return;
    }
    // Handle Enter key for submission
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleSecondaryFilterApply = (selectedFilters: any[]) => {
    const primaryFilters = currentPrimaryFiltersRef.current?.filters || [];
    
    
    // Convert primary filters from object format to array format
    let primaryFiltersArray = [];
    if (Array.isArray(primaryFilters)) {
      primaryFiltersArray = primaryFilters;
    } else if (typeof primaryFilters === 'object' && primaryFilters !== null) {
      // Extract filters from object format (e.g., {genres: ['pop'], tags2: ['Electronic', 'Sting'], ...})
      primaryFiltersArray = [];
      Object.entries(primaryFilters).forEach(([key, values]) => {
        if (Array.isArray(values) && values.length > 0) {
          values.forEach(value => {
            primaryFiltersArray.push({
              type: key === 'genres' ? 'genre' 
                  : key === 'tags' ? 'tag' 
                  : key === 'tags2' ? 'tags2' 
                  : key === 'sessionartists' ? 'sessionartist'
                  : key === 'creativedirectors' ? 'creativedirector'
                  : key === 'sessionArtists' ? 'sessionartist'  // Handle both camelCase and lowercase
                  : key === 'creativeDirectors' ? 'creativedirector'  // Handle both camelCase and lowercase
                  : key.slice(0, -1), // Convert plurals to singular, with specific exceptions
              value: value
            });
          });
        }
      });
    }
    

    // Map the selected duplicates AND pending single matches to filter format
    // Include pendingSingleSecondaryMatches because they should be applied along with user selections
    const allSecondaryFilters = [...selectedFilters, ...pendingSingleSecondaryMatches].map(match => {
      if (match.filter.multi.active) {
        return {
          type: match.filter.multi.value,
          value: match.filter.multi.data[0].value
        };
      }
      if (match.filter.subFilters.active && match.filter.subFilters.filters.length > 0) {
        const subFilter = match.filter.subFilters.filters[0];
        return {
          type: subFilter.key,
          value: subFilter.value.value
        };
      }
      return {
        type: match.collection,
        value: match.label
      };
    });

    const combinedFilters = {
      filters: [...primaryFiltersArray, ...allSecondaryFilters],
      filtersObj: currentPrimaryFiltersRef.current?.filtersObj || {},
      logic: currentPrimaryFiltersRef.current?.logic
    };
    
    
    // Apply the combined filters
    onApplyFilters(combinedFilters);
    
    // Add a message confirming the filter application
    const allAppliedFilters = [...selectedFilters, ...pendingSingleSecondaryMatches];
    const filterNames = allAppliedFilters.map(f => f.label).join(', ');
    addMessage({
      sender: 'bot',
      message: `I have included the following: ${filterNames}`
    });

    // Reset secondary selection state
    setShowSecondarySelection(false);
    setSecondaryMatches([]);
    currentPrimaryFiltersRef.current = null;
    setShouldScrollToBottom(true);
    setIsShowingSelectionUI(false);
    setPendingSingleSecondaryMatches([]);

    setTimeout(() => {
      scrollToBottom();
    }, 100);
  };

  const handleSecondaryFilterDiscard = () => {
    // Add a message indicating the user skipped the additional filters
    addMessage({
      sender: 'bot',
      message: 'Discarded.'
    });

    // Reset secondary selection state
    setShowSecondarySelection(false);
    setSecondaryMatches([]);
    currentPrimaryFiltersRef.current = null;
    setShouldScrollToBottom(true);
    setIsShowingSelectionUI(false);

    setTimeout(() => {
      scrollToBottom();
    }, 100);
  };

  // Handle audio file upload
  const handleAudioUpload = async (file: File) => {
    if (!config?.apiKey || !config.enabled) {
      addMessage({ sender: 'bot', message: 'Audio analysis is not available. Please check your configuration.' });
      return;
    }

    setAudioAnalyzing(true);
    
    addMessage({ 
      sender: 'user', 
      message: `📁 Uploaded audio file: ${file.name} (${audioAnalysisService.formatFileSize(file.size)})` 
    });
    addMessage({ sender: 'bot', message: '🎵 Analyzing your audio file...' });

    try {
      // Prepare collection data for analysis with categorized tags
      const { categorizeTags } = await import('../../utils/tagCategorizer');
      const allTags = collectionCache.tags.map((t: any) => t.displayName);
      const categorizedTags = categorizeTags(allTags);
      
      const collectionData = {
        genres: collectionCache.genres.map((g: any) => ({ displayName: g.displayName })),
        tags: collectionCache.tags.map((t: any) => ({ displayName: t.displayName })),
        categorizedTags
      };

      const analysisResult = await audioAnalysisService.analyzeAudioFile(
        file, 
        config.apiKey, 
        'find',
        collectionData,
        useSimplePrompts
      );

      // Remove the analyzing message
      useChatbotStore.setState(state => ({ messages: state.messages.slice(0, -1) }));

      // Add the analysis result as a bot message
      addMessage({ 
        sender: 'bot', 
        message: `I've analyzed your audio file: ${analysisResult.generatedQuery}` 
      });


      // Add the generated query as a user message and automatically process it
      addMessage({ sender: 'user', message: analysisResult.generatedQuery });

      // Auto-submit the generated query through the existing pipeline
      await processGeneratedQuery(analysisResult.generatedQuery);
      
    } catch (error) {
      console.error('Error analyzing audio:', error);
      useChatbotStore.setState(state => ({ messages: state.messages.slice(0, -1) }));
      addMessage({ 
        sender: 'bot', 
        message: `❌ Error analyzing audio: ${error instanceof Error ? error.message : 'Unknown error'}` 
      });
    } finally {
      setAudioAnalyzing(false);
    }
  };

  // Handle file input change
  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleAudioUpload(file);
    }
    // Reset the input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle YouTube URL detection and processing
  const detectAndProcessYouTubeUrl = async (text: string) => {
    const youtubeInfo = youtubeAudioExtractor.detectYouTubeUrl(text);
    if (youtubeInfo) {
      addMessage({ 
        sender: 'bot', 
        message: `🎬 I detected a YouTube URL. YouTube audio extraction is not yet implemented. Please upload an audio file directly for now.` 
      });
      return true;
    }
    return false;
  };

  // Process a query through the existing chatbot pipeline
  const processGeneratedQuery = async (queryText: string) => {
    if (!queryText.trim() || processingQuery) return;
    
    // Always clear previous primary filters and UI state at the start of every new query
    currentPrimaryFiltersRef.current = null;
    setSecondaryMatches([]);
    setShowSecondarySelection(false);
    setIsShowingSelectionUI(false);
    setPendingSingleSecondaryMatches([]);

    setProcessingQuery(true);

    try {
      // First, show a thinking message
      addMessage({ sender: 'bot', message: 'Searching...' });

      // Process the query using our new FilterService
      const result = await filterService.processQuery(queryText, firebase);


      // Remove the thinking message
      useChatbotStore.setState(state => ({
        messages: state.messages.slice(0, -1)
      }));

      // Add the explanation
      addMessage({ sender: 'bot', message: result.explanation });

      // Always apply primary filters immediately (including empty filters for clearing)
      if (result.filters !== undefined && result.filters !== null) {
        let filterData;
        
        if (result.directFilters && ((result.directFilters.tags && result.directFilters.tags.length > 0) || (result.directFilters.tags2 && result.directFilters.tags2.length > 0) || (result.directFilters.genres && result.directFilters.genres.length > 0) || (result.directFilters.keys && result.directFilters.keys.length > 0) || (result.directFilters.agencies && result.directFilters.agencies.length > 0) || (result.directFilters.clients && result.directFilters.clients.length > 0) || (result.directFilters.producers && result.directFilters.producers.length > 0) || (result.directFilters.studios && result.directFilters.studios.length > 0) || (result.directFilters.composers && result.directFilters.composers.length > 0) || (result.directFilters.creativeDirectors && result.directFilters.creativeDirectors.length > 0) || (result.directFilters.sessionArtists && result.directFilters.sessionArtists.length > 0) || (result.directFilters.license && result.directFilters.license !== ''))) {
          // Use direct filters format when available
          filterData = { 
            filters: result.directFilters, 
            filtersObj: result.filters, 
            logic: result.logic 
          };
        } else {
          // Use keyword-based filters format
          filterData = { 
            filters: result.filters, 
            filtersObj: result.filters, 
            logic: result.logic 
          };
        }
        
        
        onApplyFilters(filterData);
        
        // Check if this is a filter clear operation (intent=3)
        // Intent=3 results in all empty fields according to the prompt
        const isFilterClear = Array.isArray(result.filters) && result.filters.length === 0 &&
          (!result.keywords || result.keywords.length === 0) &&
          result.explanation && (
            result.explanation.toLowerCase().includes('clear') ||
            result.explanation.toLowerCase().includes('reset') ||
            result.explanation.toLowerCase().includes('start')
          );
        
        if (isFilterClear) {
          // Add hidden reset flag to mark this point in chat history
          addFilterResetFlag();
        }
        
        // Store primary filters for later combination with secondary filters
        currentPrimaryFiltersRef.current = filterData;
      }

      // Check for secondary matches and find duplicates
      if (result.secondaryMatches && result.secondaryMatches.length > 0) {
        
        // Group secondary matches by collection
        const matchesByCollection = result.secondaryMatches.reduce((acc: { [key: string]: any[] }, match) => {
          const collection = match.collection;
          if (!acc[collection]) {
            acc[collection] = [];
          }
          acc[collection].push(match);
          return acc;
        }, {});
        

        // Find collections with multiple matches (duplicates)
        const duplicateMatches = Object.values(matchesByCollection)
          .filter(matches => matches.length > 1)
          .flat();

        // Find collections with single matches
        const singleMatches = Object.values(matchesByCollection)
          .filter(matches => matches.length === 1)
          .flat();

        // Check for true cross-collection duplicates where a SINGLE keyword matches multiple items
        // This should only trigger when one search term like "Craig" matches multiple items across collections
        const keywordDuplicates = new Map<string, any[]>();
        
        // Group matches by the original keyword that caused the match
        result.secondaryMatches.forEach(match => {
          const label = match.label.toLowerCase();
          
          // Find which specific keyword from the original keywords array this match corresponds to
          const matchingKeyword = result.keywords?.find(keyword => {
            const lowerKeyword = keyword.toLowerCase();
            // Use exact match or word boundary match to be more precise
            return label.includes(lowerKeyword) || lowerKeyword.includes(label);
          });
          
          if (matchingKeyword) {
            const normalizedKeyword = matchingKeyword.toLowerCase();
            if (!keywordDuplicates.has(normalizedKeyword)) {
              keywordDuplicates.set(normalizedKeyword, []);
            }
            keywordDuplicates.get(normalizedKeyword)!.push(match);
          }
        });

        // Find keywords that have multiple matches (true duplicates that need user disambiguation)
        const crossCollectionDuplicates = Array.from(keywordDuplicates.entries())
          .filter(([keyword, matches]) => {
            // Only consider it a duplicate requiring user selection if:
            // 1. A SINGLE keyword matched multiple items, OR
            // 2. Multiple items from the same collection matched the keyword
            return matches.length > 1;
          })
          .flatMap(([keyword, matches]) => matches);

        // Check for matches between primary and secondary collections
        const primarySecondaryDuplicates = result.secondaryMatches.filter(match => {
          const label = match.label.toLowerCase();
          // Check if any primary filter value matches this secondary match
          return result.filters.some(primaryFilter => {
            if (primaryFilter.type === 'date' || primaryFilter.type === 'year') {
              return false; // Skip date filters for duplicate detection
            }
            const primaryValue = Array.isArray(primaryFilter.value) 
              ? primaryFilter.value[0].toLowerCase()
              : primaryFilter.value.toLowerCase();
            return primaryValue.includes(label) || label.includes(primaryValue);
          });
        });

        const hasCrossCollectionDuplicates = crossCollectionDuplicates.length > 0;
        const hasPrimarySecondaryDuplicates = primarySecondaryDuplicates.length > 0;

        // If we have duplicate matches (either within collection, cross-collection for same keyword, or between primary/secondary), show the selection UI
        if (duplicateMatches.length > 0 || hasCrossCollectionDuplicates || hasPrimarySecondaryDuplicates) {
          // Clean up any existing scroll animation
          cleanupScroll();
          
          const allDuplicates = Array.from(new Set([
            ...duplicateMatches,
            ...crossCollectionDuplicates,
            ...primarySecondaryDuplicates
          ]));
          
          // Remove duplicate items from single matches to avoid applying them automatically
          const actualSingleMatches = singleMatches.filter(singleMatch => 
            !allDuplicates.some(duplicate => 
              duplicate.label === singleMatch.label && duplicate.collection === singleMatch.collection
            )
          );
          
          setSecondaryMatches(allDuplicates);
          setPendingSingleSecondaryMatches(actualSingleMatches);
          setShowSecondarySelection(true);
          
          // Add a message about the duplicate matches
          const matchTypes = Array.from(new Set(allDuplicates.map((m: any) => m.collection)));
          
          // Find which keywords had duplicates for a better message
          const keywordsWithDuplicates = Array.from(keywordDuplicates.entries())
            .filter(([keyword, matches]) => matches.length > 1)
            .map(([keyword, matches]) => keyword);
          
          const duplicateKeyword = keywordsWithDuplicates.length > 0 ? keywordsWithDuplicates[0] : 'your search';
          
          addMessage({
            sender: 'bot',
            message: `I found multiple matches for "${duplicateKeyword}" in ${matchTypes.join(', ')}. Please select which ones you'd like to include:`
          });
          
          // Use a Promise-based delay function
          const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
          
          // Execute the scroll sequence with proper delays
          (async () => {
            
            // Wait for initial render and message to be visible
            await delay(3000);
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            
            // Wait longer for message scroll to complete and user to read
            await delay(24000);
            scrollToSelectionUI();
          })();
        } else {
          // For non-selection UI cases, scroll to bottom
          setTimeout(() => {
            scrollToBottom();
          }, 100);
          // Re-enable scroll to bottom for other messages
          setShouldScrollToBottom(true);
          // If we have single matches and no duplicates, apply them immediately
          const singleFilters = singleMatches.map((match, index) => {
            let filter;
            if (match.filter.multi.active) {
              filter = {
                type: match.filter.multi.value,
                value: match.filter.multi.data[0].value
              };
            } else {
              const subFilter = match.filter.subFilters.filters[0];
              filter = {
                type: subFilter.key,
                value: subFilter.value.value
              };
            }
            return filter;
          });

          // Convert primary filters from object format to array format (same logic as handleSecondaryFilterApply)
          const primaryFilters = currentPrimaryFiltersRef.current?.filters || [];
          let primaryFiltersArray = [];
          if (Array.isArray(primaryFilters)) {
            primaryFiltersArray = primaryFilters;
          } else if (typeof primaryFilters === 'object' && primaryFilters !== null) {
            // Extract filters from object format (e.g., {genres: ['pop'], tags2: ['Electronic', 'Sting'], ...})
            primaryFiltersArray = [];
            Object.entries(primaryFilters).forEach(([key, values]) => {
              if (Array.isArray(values) && values.length > 0) {
                values.forEach(value => {
                  primaryFiltersArray.push({
                    type: key === 'genres' ? 'genre' 
                        : key === 'tags' ? 'tag' 
                        : key === 'tags2' ? 'tags2' 
                        : key === 'sessionartists' ? 'sessionartist'
                        : key === 'creativedirectors' ? 'creativedirector'
                        : key === 'sessionArtists' ? 'sessionartist'  // Handle both camelCase and lowercase
                        : key === 'creativeDirectors' ? 'creativedirector'  // Handle both camelCase and lowercase
                        : key.slice(0, -1), // Convert plurals to singular, with specific exceptions
                    value: value
                  });
                });
              }
            });
          }

          // Combine with current filters and apply
          const combinedFilters = {
            filters: [...primaryFiltersArray, ...singleFilters],
            filtersObj: currentPrimaryFiltersRef.current?.filtersObj || result.filters,
            logic: currentPrimaryFiltersRef.current?.logic || result.logic
          };

          onApplyFilters(combinedFilters);

          // Note: Single secondary matches are applied silently like primary filters
          // Only duplicate resolution should show "Applied filter:" messages
        }
      }
    } catch (error) {
      console.error('Error processing query:', error);
      
      // Remove the thinking message
      useChatbotStore.setState(state => ({
        messages: state.messages.slice(0, -1)
      }));
      
      addMessage({ 
        sender: 'bot', 
        message: 'Sorry, I had trouble processing your request. Please try again or use the filters directly.' 
      });
    } finally {
      setProcessingQuery(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || processingQuery || audioAnalyzing) return;
    
    // Check for YouTube URLs first
    const hasYouTubeUrl = await detectAndProcessYouTubeUrl(input);
    if (hasYouTubeUrl) {
      setInput('');
      return;
    }

    const userMessage = input.trim();
    
    
    setInput('');
    addMessage({ sender: 'user', message: userMessage });
    
    // Process the query through the existing pipeline
    await processGeneratedQuery(userMessage);
  };

  // Add effect to focus input when chatbot opens
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen]);

  // Clear chat on mount to ensure history does not persist between page navigations
  useEffect(() => {
    clearChat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isOpen) return null;

  return (
    <div className="chatbot-panel">
      <div className="chatbot-header">
        <h3>Vault Assistant</h3>
      </div>
      <div className="chatbot-messages">
        {messages.length === 0 ? (
          <div className="chatbot-welcome">
            <p>Hi{firstName ? ` ${firstName}` : ''}, I'm your Vault Assistant. What can I help you find?</p>
            <div className="capability-section">
              <h4>Genre & Style</h4>
              <ul>
                <li>"Find me upbeat rock tracks"</li>
                <li>"Show me upbeat electronic music"</li>
                <li>"I need a calm piano piece"</li>
              </ul>
            </div>
            <div className="capability-section">
              <h4>Technical Details</h4>
              <ul>
                <li>"Tracks around 120 BPM"</li>
                <li>"Music in the key of C"</li>
                <li>"Tracks from 2020"</li>
              </ul>
            </div>
            

           
          </div>
        ) : (
          messages.map(msg => (
            <ChatMessage 
              key={msg.id} 
              message={msg.message} 
              sender={msg.sender} 
            />
          ))
        )}
        {showSecondarySelection && (
          <div ref={selectionUIRef}>
            <SecondaryFilterSelection
              matches={secondaryMatches}
              onApply={handleSecondaryFilterApply}
              onDiscard={handleSecondaryFilterDiscard}
            />
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <form onSubmit={handleSubmit} className="chatbot-input">
        <div className="input-row">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={processingQuery || audioAnalyzing ? 'Please wait...' : 'Search for music...'}
            disabled={processingQuery || audioAnalyzing || !config?.enabled}
            rows={1}
          />
          <div className="input-buttons">
            {/* Temporarily commented out - audio upload button for Find a Track chatbot
            <button 
              type="button"
              className="audio-upload-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={processingQuery || audioAnalyzing || !config?.enabled}
              title="Upload audio file for analysis"
            >
              {audioAnalyzing ? (
                <i className="tim-icons icon-refresh-02 rotating"></i>
              ) : (
                <i className="tim-icons icon-upload" />
              )}
            </button>
            */}
            <button 
              type="submit" 
              disabled={processingQuery || audioAnalyzing || !input.trim() || !config?.enabled}
            >
              {processingQuery ? (
                <i className="tim-icons icon-refresh-02 rotating"></i>
              ) : (
                <i className="tim-icons icon-send"></i>
              )}
            </button>
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".mp3,.wav,.m4a"
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />
      </form>
      {!config?.enabled && (
        <div className="chatbot-disabled-notice">
          <p>Chat assistance is currently disabled. Please check back later.</p>
        </div>
      )}
    </div>
  );
};

export default ChatbotPanel;